import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { ICompetition } from 'src/interfaces/Competition';
import { TechnicalType } from 'src/interfaces/TechnicalType';

import EditionYearFilter from './EditionYearFilter';

interface Props {
  selectedYear: number | null;
  selectedCompetitionName?: string;
  competitionsForEdition: ICompetition[];
  currentLang: LanguageKeys;
  onYearChange: (year: number | null) => void;
  onCompetitionChange: (competitionName: string | undefined) => void;
  allowAllYears?: boolean;
  allowAllCompetitions?: boolean;
  showDistanceFilter?: boolean;
  distancesForCompetition?: TechnicalType[];
  selectedDistanceName?: string;
  onDistanceChange?: (distanceName: string | undefined) => void;
  allowAllDistances?: boolean;
}

export default function EditionCompetitionToolbar({
  selectedYear,
  selectedCompetitionName,
  competitionsForEdition,
  currentLang,
  onYearChange,
  onCompetitionChange,
  allowAllYears = false,
  allowAllCompetitions = true,
  showDistanceFilter = false,
  distancesForCompetition = [],
  selectedDistanceName,
  onDistanceChange,
  allowAllDistances = true,
}: Props) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, width: '100%' }}>
      <EditionYearFilter
        selectedYear={selectedYear}
        onYearChange={onYearChange}
        allowAllYears={allowAllYears}
        id="toolbar-edition-year"
      />
      {competitionsForEdition.length > 0 ? (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="toolbar-competition-label" shrink>
            {t('race.results.competition')}
          </InputLabel>
          <Select
            labelId="toolbar-competition-label"
            label={t('race.results.competition')}
            value={selectedCompetitionName ?? ''}
            onChange={e => onCompetitionChange(e.target.value || undefined)}
            displayEmpty={allowAllCompetitions}
            renderValue={selected => {
              if (!selected) {
                return allowAllCompetitions ? t('base.all') : '';
              }
              const comp = competitionsForEdition.find(c => c.name === selected);
              return comp ? comp.i18n[currentLang] : selected;
            }}
          >
            {allowAllCompetitions ? <MenuItem value="">{t('base.all')}</MenuItem> : null}
            {competitionsForEdition.map(c => (
              <MenuItem key={c.id} value={c.name}>
                {c.i18n[currentLang]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {showDistanceFilter && distancesForCompetition.length > 0 ? (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="toolbar-distance-label" shrink>
            {t('events.distance')}
          </InputLabel>
          <Select
            labelId="toolbar-distance-label"
            label={t('events.distance')}
            value={selectedDistanceName ?? ''}
            onChange={e => onDistanceChange?.(e.target.value || undefined)}
            displayEmpty={allowAllDistances}
            renderValue={selected => {
              if (!selected) {
                return allowAllDistances ? t('base.all') : '';
              }
              const dist = distancesForCompetition.find(d => d.name === selected);
              return dist ? dist.i18n[currentLang] : selected;
            }}
          >
            {allowAllDistances ? <MenuItem value="">{t('base.all')}</MenuItem> : null}
            {distancesForCompetition.map(d => (
              <MenuItem key={d.id} value={d.name}>
                {d.i18n[currentLang]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Box>
  );
}
